import { logEvent } from '@coverright/shared/analytics';
import { enqueueSnackbar } from 'notistack';

import { removePlansFromApolloCache } from './lib';
import {
  CalcDrugFrequencyPeriodInput,
  FrequencyPeriodInput,
  PreferredDrugInput
} from '@coverright/data-access/types/enrollment';
import differenceWith from 'lodash/differenceWith';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import {
  mainBackendApolloCache,
  enrollmentApolloClient,
  mainBackendApolloClient
} from '@coverright/data-access/apollo-clients';
import { NonOptional } from '../../../index';
import { DrugFragmentDoc, MedicareQuoteQuery } from '@coverright/data-access/medicare';
import { DrugOutput } from '@coverright/data-access/types/medicare';
import { usePatchClientPersonalInfoMutation } from '@coverright/data-access/enrollment';

export function useSaveDrugs(quote: NonOptional<MedicareQuoteQuery['medicareQuote']>) {
  const [mutation] = usePatchClientPersonalInfoMutation({ client: enrollmentApolloClient });

  return async (drugs: DrugOutput[]) => {
    if (quote) {
      const preferredDrugs = drugs.map(convertPreferredDrugInput);

      try {
        await mutation({
          variables: {
            clientId: quote.clientId,
            preferredDrugs,
          },
          optimisticResponse: (vars) => {
            return {
              patchClientPersonalInfo: true,
            };
          },
          update: () => {
            mainBackendApolloCache.modify({
              id: mainBackendApolloCache.identify(quote),
              fields: {
                preferredDrugs(cachedPreferredDrugsRefs, {readField, }) {
                  // handle adding new drugs
                  if (drugs.length > cachedPreferredDrugsRefs.length) {
                    const addedDrugs = differenceWith(drugs, cachedPreferredDrugsRefs, compare(readField));
                    const addedDrugsRefs: any[] = [];

                    addedDrugs.forEach(drug => {
                      const newRef = mainBackendApolloCache.writeFragment({
                        data: { ...drug, __typename: 'DrugOutput' },
                        fragment: DrugFragmentDoc
                      })
                      addedDrugsRefs.push(newRef);
                    })
                    return [...cachedPreferredDrugsRefs, ...addedDrugsRefs]

                  } else if (drugs.length < cachedPreferredDrugsRefs.length) {
                    // handle remove drugs
                    const removedDrugs = differenceWith(cachedPreferredDrugsRefs, drugs, (a, b) => compare(readField)(b, a));
                    return cachedPreferredDrugsRefs.filter((drugRef: any) => !removedDrugs.some(d => isEqual(d, drugRef)));

                  } else {
                    // handle edit drugs
                    cachedPreferredDrugsRefs.forEach((ref: any) => {
                      mainBackendApolloCache.updateFragment({
                        id: mainBackendApolloCache.identify(ref),
                        fragment: DrugFragmentDoc
                      }, (data: DrugOutput | null) => {
                        const drug = drugs.find(d => d.rxcui === data?.rxcui && d.packRxcui === data.packRxcui)
                        if (data && drug) {
                          return {
                            ...data,
                            quantity: drug.quantity,
                            frequency: drug.frequency,
                            purchaseFrequency: drug.purchaseFrequency,
                          }
                        }
                        return data
                      })
                    })

                  }
                  return cachedPreferredDrugsRefs
                },
              }
            })
          },
        });

        setTimeout(() => {
          removePlansFromApolloCache();

          mainBackendApolloClient.refetchQueries({
            include: ['medicareQuote', 'pdpQuote'],
          });
        }, 1000)

        if (preferredDrugs.length) {
          logEvent('Drugs Added', {
            rxcuis: preferredDrugs,
            location: 'Plan finder',
          });
        }
        enqueueSnackbar('Drugs has been saved', { variant: 'success' });
      } catch (e) {
        enqueueSnackbar('Error saving drugs', { variant: 'error' });
      }
    } else {
      throw Error('Medicare quote is not exists');
    }
  };
}

const compare = (readField: any) => (a: DrugOutput, b: any) => {
  const refObj = {rxcui: readField('rxcui', b), packRxcui: readField('packRxcui', b)};
  return isEqual(pick(a, ['rxcui', 'packRxcui']), refObj);
}

function convertPreferredDrugInput(
  drugInfo: DrugOutput
): PreferredDrugInput {
  return {
    dosageRxcui: drugInfo.packRxcui,
    productRxcui: drugInfo.rxcui,
    quantity: drugInfo.quantity,
    frequency: drugInfo.frequency as unknown as CalcDrugFrequencyPeriodInput,
    purchaseFrequency:
      drugInfo.purchaseFrequency as unknown as FrequencyPeriodInput,
  };
}
